import { Store } from '../types/location.types';

export interface BookingState {
  // Pet selection
  selectedPets: Array<{
    id?: number;
    type?: 'dog' | 'cat';
    name?: string;
    breed?: string;
    breedId?: number;
    gender?: string;
    weight?: string;
    age?: number;
    dob?: string;
    aggression?: string;
    info?: string;
  }>;
  
  // Service selection
  petServices: Record<string, Array<{
    serviceId: string;
    price: number;
    name: string;
    categoryName: string;
    pricingOptionId: string;
    pricingDetails: {
      name?: string;
      weightFrom?: number | null;
      weightTo?: number | null;
      duration?: number;
      note?: string;
    };
    groomingServiceAppliedStoresAndUsers?: Array<{
      storeId: number;
      userIds: number[];
    }>;
  }>>;
  
  // Date/time/staff selection
  selectedDate?: string;
  selectedTime?: string;
  selectedStaff?: string;
  selectedStaffName?: string;
  
  // Store and pricing
  selectedStore: Store | null;
  totalPrice?: number;
  
  // Flow tracking
  currentStep: 'pets' | 'services' | 'datetime' | 'confirmation';
  currentPetIndex: number;
  bookingStep: number;

  /**
   * Pets "Make it extra for Leo?" has asked this booking (SF 64: once per
   * pet per booking; G34). Cleared with the booking, and at sign-out with it (G1).
   */
  upsellAsked?: string[];

  // Metadata
  timestamp: number;
  userId?: string; // For validation
}

export interface BookingStateManager {
  getBookingState(): BookingState | null;
  saveBookingState(state: Partial<BookingState>): void;
  updateBookingState(updates: Partial<BookingState>): void;
  clearBookingState(): void;
  isStateValid(): boolean;
  isStepAccessible(step: string, state: BookingState): boolean;
}

class BookingStateService implements BookingStateManager {
  private readonly STORAGE_KEY = 'booking_state';
  private readonly CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes
  
  /**
   * Get booking state from localStorage
   */
  getBookingState(): BookingState | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;
      
      const state: BookingState = JSON.parse(stored);
      
      // Check if state is expired
      if (Date.now() - state.timestamp > this.CACHE_EXPIRY_MS) {
        this.clearBookingState();
        return null;
      }
      
      return state;
    } catch (error) {
      console.error('Error getting booking state:', error);
      this.clearBookingState();
      return null;
    }
  }
  
  /**
   * Save complete booking state to localStorage
   */
  saveBookingState(state: Partial<BookingState>): void {
    try {
      const currentState = this.getBookingState();
      const newState: BookingState = {
        // Defaults
        selectedPets: [],
        petServices: {},
        currentStep: 'pets',
        currentPetIndex: 0,
        bookingStep: 0,
        selectedStore: null,
        
        // Merge with existing state
        ...currentState,
        
        // Apply new updates
        ...state,
        
        // Always update timestamp
        timestamp: Date.now()
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(newState));
    } catch (error) {
      console.error('Error saving booking state:', error);
    }
  }
  
  /**
   * Update specific fields in booking state
   */
  updateBookingState(updates: Partial<BookingState>): void {
    const currentState = this.getBookingState();
    if (currentState) {
      this.saveBookingState({ ...currentState, ...updates });
    } else {
      this.saveBookingState(updates);
    }
  }
  
  /**
   * Clear booking state from localStorage
   */
  clearBookingState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing booking state:', error);
    }
  }
  
  /**
   * Check if current booking state is valid and not expired
   */
  isStateValid(): boolean {
    const state = this.getBookingState();
    return state !== null;
  }
  
  /**
   * Check if a specific step is accessible based on current state
   */
  isStepAccessible(step: string, state: BookingState): boolean {
    switch (step) {
      case 'pets':
        // Always accessible - entry point
        return true;
        
      case 'services':
        // The services step is always open: pets are added and picked on it.
        return true;
        
      case 'datetime':
        // Datetime step requires at least ONE pet with a service. A customer
        // may have many pets but only book services for some of them, so we do
        // NOT require every selected pet to have a service — that made the step
        // unreachable on direct navigation (e.g. returning from checkout) and
        // dropped the user back on an empty service page.
        if (!state.selectedPets || state.selectedPets.length === 0) {
          return false;
        }
        return Object.values(state.petServices || {}).some(
          services => Array.isArray(services) && services.length > 0
        );
        
      case 'confirmation':
        // Confirmation requires valid date/time/staff selection
        return this.isStepAccessible('datetime', state) && 
               !!state.selectedDate && 
               !!state.selectedTime && 
               !!state.selectedStaff;
        
      default:
        return false;
    }
  }
  
  /**
   * Get the appropriate redirect step for current state
   */
  getRedirectStep(targetStep: string): string {
    const state = this.getBookingState();
    if (!state) return 'services';
    
    // Check accessibility in order based on target step
    switch (targetStep) {
      case 'datetime':
        if (!this.isStepAccessible('datetime', state)) {
          return 'services';
    }
        break;
      case 'confirmation':
        if (!this.isStepAccessible('confirmation', state)) {
    if (!this.isStepAccessible('datetime', state)) {
      return 'services';
          }
          return 'datetime';
        }
        break;
    }
    
    // If step is accessible, allow target step
    return targetStep;
  }
  
  /**
   * Initialize booking state with store information.
   *
   * Called on every BookingFlow mount. It must NOT wipe an in-progress
   * booking — otherwise returning from checkout (which remounts BookingFlow)
   * erases the cart the user just had. So if there's already a cart (any pet
   * with services), we keep it and only refresh the store/user reference;
   * otherwise we initialize a fresh state.
   */
  initializeBookingState(selectedStore: Store, userId?: string): void {
    const existing = this.getBookingState();
    const hasCart = !!existing?.petServices &&
      Object.values(existing.petServices).some(s => Array.isArray(s) && s.length > 0);

    if (existing && hasCart) {
      this.updateBookingState({ selectedStore, userId });
      return;
    }

    this.saveBookingState({
      selectedStore,
      userId,
      currentStep: 'pets',
      selectedPets: [],
      petServices: {},
      currentPetIndex: 0,
      bookingStep: 0
    });
  }
}

// Export singleton instance
export const bookingStateService = new BookingStateService();

// Export convenience functions
export const {
  getBookingState,
  saveBookingState,
  updateBookingState,
  clearBookingState,
  isStateValid,
  isStepAccessible
} = bookingStateService;

export default bookingStateService;
